import React, { useEffect, useState, useRef, useMemo, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { productsApi, Product, PlatformKey } from '../../lib/api/products';
import {
    Plus,
    Globe,
    ArrowLeft,
    Save,
    Trash2,
    X,
    Settings,
    Map as MapIcon,
    Copy,
    Store,
    ShoppingBag,
    Check,
    ChevronDown,
} from 'lucide-react';
import { FeatureManagerV2, FeatureManagerHandle } from './components/FeatureManager';
import { ConfirmationModal } from '../../components/ConfirmationModal';

// ============================================================================
// CONFIGURATION
// ============================================================================

const AVAILABLE_COUNTRIES = [
    { code: 'US', name: 'United States', currency: 'USD', symbol: '$', emoji: '🇺🇸' },
    { code: 'GB', name: 'United Kingdom', currency: 'GBP', symbol: '£', emoji: '🇬🇧' },
    { code: 'EU', name: 'European Union', currency: 'EUR', symbol: '€', emoji: '🇪🇺' },
    { code: 'FR', name: 'France', currency: 'EUR', symbol: '€', emoji: '🇫🇷' },
    { code: 'CA', name: 'Canada', currency: 'CAD', symbol: 'C$', emoji: '🇨🇦' },
    { code: 'AU', name: 'Australia', currency: 'AUD', symbol: 'A$', emoji: '🇦🇺' },
    { code: 'IN', name: 'India', currency: 'INR', symbol: '₹', emoji: '🇮🇳' },
    { code: 'JP', name: 'Japan', currency: 'JPY', symbol: '¥', emoji: '🇯🇵' },
    { code: 'BR', name: 'Brazil', currency: 'BRL', symbol: 'R$', emoji: '🇧🇷' },
    { code: 'MX', name: 'Mexico', currency: 'MXN', symbol: '$', emoji: '🇲🇽' },
    { code: 'DE', name: 'Germany', currency: 'EUR', symbol: '€', emoji: '🇩🇪' },
    { code: 'TN', name: 'Tunisia', currency: 'TND', symbol: 'DT', emoji: '🇹🇳' },
];

const ProductIdDisplay: React.FC<{ id: string }> = ({ id }) => {
    const [status, setStatus] = useState<'copy' | 'copied'>('copy');

    return (
        <div
            className="group relative ml-3 inline-flex items-center"
            onClick={() => {
                navigator.clipboard.writeText(id);
                setStatus('copied');
                setTimeout(() => setStatus('copy'), 2000); // Reset after 2s
            }}
            onMouseLeave={() => {
                if (status === 'copied') setTimeout(() => setStatus('copy'), 300);
            }}
        >
            <span className="text-sm font-normal text-neutral-400 font-mono cursor-pointer hover:text-neutral-600 transition-colors">
                {id}
            </span>
            {/* Custom Tooltip */}
            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2 py-1 bg-neutral-800 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap shadow-lg z-50">
                {status === 'copy' ? 'Copy' : 'Copied!'}
                {/* Little arrow pointing up */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-neutral-800"></div>
            </div>
        </div>
    );
};

// ============================================================================
// COMPONENTS
// ============================================================================

interface DuplicateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (name: string) => void;
    initialName: string;
    isSubmitting: boolean;
}

const DuplicateModal: React.FC<DuplicateModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    initialName,
    isSubmitting
}) => {
    const [name, setName] = useState(initialName);
    const titleRef = useRef(null);

    useEffect(() => {
        if (isOpen) setName(`${initialName} (Copy)`);
    }, [isOpen, initialName]);

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[100]" onClose={onClose} initialFocus={titleRef}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 transform transition-all border border-neutral-100">
                                {/* Icon */}
                                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-accent-primary/10 rounded-full transition-colors duration-300">
                                    <Copy className="w-8 h-8 text-accent-primary" />
                                </div>

                                {/* Content */}
                                <div className="text-center mb-8">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-xl font-bold text-neutral-900 mb-2 focus:outline-none"
                                        ref={titleRef}
                                    >
                                        Duplicate Product
                                    </Dialog.Title>
                                    <p className="text-sm text-neutral-500 mb-4">
                                        Enter a name for the new product. All features and settings will be copied.
                                    </p>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary outline-none transition-all placeholder:text-neutral-300"
                                        placeholder="Product Name"
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && name.trim()) {
                                                onConfirm(name);
                                            }
                                        }}
                                    />
                                </div>

                                {/*Buttons */}
                                <div className="flex gap-3">
                                    <button
                                        onClick={onClose}
                                        disabled={isSubmitting}
                                        className="flex-1 px-4 py-3 text-sm font-bold text-neutral-500 bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-200 transition-all active:scale-[0.98] disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => onConfirm(name)}
                                        disabled={isSubmitting || !name.trim()}
                                        className="flex-1 px-4 py-3 text-sm font-bold text-white bg-accent-primary hover:bg-accent-primary/90 rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-primary transition-all shadow-lg shadow-accent-primary/20 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                <span>Copying...</span>
                                            </>
                                        ) : (
                                            'Duplicate'
                                        )}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

// ============================================================================
// COMPONENT
// ============================================================================

export const ProductDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNew = id === 'new';

    const [product, setProduct] = useState<Partial<Product>>({
        name: '',
        description: '',
        isActive: true,
        isDefault: false,
        availableOn: ['website'],
        variants: []
    });
    const [platformsMenuOpen, setPlatformsMenuOpen] = useState(false);
    const platformsMenuRef = useRef<HTMLDivElement | null>(null);

    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState<'general' | 'pricing' | 'features'>(isNew ? 'general' : 'pricing');
    const [newFeatureInput, setNewFeatureInput] = useState('');

    // Pricing UI State
    const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
    const [isAddRegionOpen, setIsAddRegionOpen] = useState(false);
    const [activePricingTab, setActivePricingTab] = useState<'prices' | 'settings'>('prices');

    // State for dirty checking
    const [originalProduct, setOriginalProduct] = useState<Partial<Product> | null>(null);
    const featureManagersRef = useRef<Map<string, FeatureManagerHandle>>(new Map());
    const [dirtyFeatures, setDirtyFeatures] = useState<Set<string>>(new Set());

    // Modal State
    const [isDuplicateRegionModalOpen, setIsDuplicateRegionModalOpen] = useState(false);
    const [regionToDuplicate, setRegionToDuplicate] = useState<string | null>(null);

    const [modalConfig, setModalConfig] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        variant: 'danger' | 'warning' | 'info';
        confirmText?: string;
        cancelText?: string;
        onConfirm: () => void;
    }>({
        isOpen: false,
        title: '',
        message: '',
        variant: 'warning',
        onConfirm: () => { },
    });

    const showConfirmation = (
        title: string,
        message: string,
        onConfirm: () => void,
        variant: 'danger' | 'warning' | 'info' = 'warning',
        confirmText = 'Confirm'
    ) => {
        setModalConfig({
            isOpen: true,
            title,
            message,
            variant,
            onConfirm,
            confirmText,
            cancelText: 'Cancel'
        });
    };

    const showAlert = (title: string, message: string, variant: 'danger' | 'warning' | 'info' = 'info') => {
        setModalConfig({
            isOpen: true,
            title,
            message,
            variant,
            confirmText: 'OK',
            cancelText: '', // Hide cancel if possible, or just ignore
            onConfirm: () => { }
        });
    };

    const isProductDirty = useMemo(() => {
        if (!originalProduct) return isNew && !!product.name;
        // Simple deep comparison
        return JSON.stringify(product) !== JSON.stringify(originalProduct);
    }, [product, originalProduct, isNew]);

    const isDirty = isProductDirty || dirtyFeatures.size > 0;

    const handleFeatureDirty = (key: string, dirty: boolean) => {
        setDirtyFeatures(prev => {
            const next = new Set(prev);
            if (dirty) next.add(key);
            else next.delete(key);
            return next;
        });
    };

    useEffect(() => {
        if (!isNew && id) {
            loadProduct(id);
        }
    }, [id, isNew]);

    useEffect(() => {
        if (!platformsMenuOpen) return;
        const onClick = (e: MouseEvent) => {
            if (platformsMenuRef.current && !platformsMenuRef.current.contains(e.target as Node)) {
                setPlatformsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [platformsMenuOpen]);

    const togglePlatform = (key: PlatformKey) => {
        setProduct(prev => {
            const current = (prev.availableOn ?? ['website']) as PlatformKey[];
            const next = current.includes(key)
                ? current.filter(p => p !== key)
                : [...current, key];
            return { ...prev, availableOn: next };
        });
    };

    const PLATFORM_OPTIONS: { key: PlatformKey; label: string; icon: typeof Store }[] = [
        { key: 'website', label: 'Dashbal Website', icon: Store },
        { key: 'shopify', label: 'Dashbal Shopify App', icon: ShoppingBag },
    ];

    const loadProduct = async (productId: string) => {
        try {
            setLoading(true);
            const data = await productsApi.get(productId);
            setProduct(data);
            setOriginalProduct(JSON.parse(JSON.stringify(data)));
            if (data.variants && data.variants.length > 0) {
                setSelectedRegion(data.variants[0].id);
            }
        } catch (err) {
            console.error('Failed to load product', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            if (isNew) {
                // For new product, we save product first then features if any (though likely none yet)
                const created = await productsApi.create(product as any);
                setProduct(prev => ({ ...prev, id: created.id }));
                // Save any features that might have been configured (Global Defaults)
                await Promise.all(
                    Array.from(featureManagersRef.current.values()).map(manager => manager.save())
                );
                navigate(`/payments/products/${created.id}?tab=pricing`, { replace: true });
            } else {
                if (isProductDirty) {
                    await productsApi.update(id!, product);
                }

                // Save all features
                await Promise.all(
                    Array.from(featureManagersRef.current.values()).map(manager => manager.save())
                );

                // Refresh to ensure sync
                loadProduct(id!);
            }
        } catch (err) {
            console.error('Failed to save product', err);
            showAlert('Error', 'Failed to save product', 'danger');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        showConfirmation(
            'Delete Product',
            'Are you sure you want to delete this product? This action cannot be undone.',
            async () => {
                try {
                    await productsApi.delete(id!);
                    navigate('/payments/products');
                } catch (err) {
                    console.error(err);
                    showAlert('Error', 'Failed to delete product', 'danger');
                }
            },
            'danger',
            'Delete'
        );
    };



    // Duplicate Modal State
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
    const [isDuplicating, setIsDuplicating] = useState(false);

    const handleDuplicateConfirm = async (name: string) => {
        try {
            setIsDuplicating(true);
            const newProduct = await productsApi.duplicate(id!, name);
            navigate(`/payments/products/${newProduct.id}`);
            // No success message needed as per request
        } catch (err) {
            console.error(err);
            showAlert('Error', 'Failed to duplicate product', 'danger');
        } finally {
            setIsDuplicating(false);
            setIsDuplicateModalOpen(false);
        }
    };

    // ========================================================================
    // PRICING LOGIC
    // ========================================================================

    const handleAddVariant = (country: typeof AVAILABLE_COUNTRIES[0], copyFromVariantId?: string) => {
        let newVariant: any = {
            id: `temp_${Date.now()}`,
            productId: product.id || '',
            country: country.code,
            currency: country.currency,
            prices: [],
            isActive: true,
            slogan: '',
            displayOrder: 0,
            isHighlighted: false,
            highlightText: '',
            marketingFeatures: '[]'
        };

        if (copyFromVariantId) {
            const baseVariant = product.variants?.find(v => v.id === copyFromVariantId);
            if (baseVariant) {
                newVariant = {
                    ...newVariant,
                    name: baseVariant.name,
                    description: baseVariant.description,
                    slogan: baseVariant.slogan,
                    displayOrder: baseVariant.displayOrder,
                    isHighlighted: baseVariant.isHighlighted,
                    highlightText: baseVariant.highlightText,
                    marketingFeatures: baseVariant.marketingFeatures,
                    prices: baseVariant.prices.map(p => ({
                        ...p,
                        id: `temp_p_${Date.now()}_${Math.random().toString(36).substring(7)}`,
                        currency: country.currency // Convert currency to match new country
                    }))
                };
            }
        }

        const updatedVariants = [...(product.variants || []), newVariant];
        setProduct({ ...product, variants: updatedVariants });
        setSelectedRegion(newVariant.id);
    };

    const handleAddPrice = (variantId: string) => {
        const updatedVariants = product.variants?.map(v => {
            if (v.id === variantId) {
                const hasMonthly = v.prices.some(p => p.interval === 'month');
                const newPrice = {
                    id: `temp_p_${Date.now()}`,
                    variantId: v.id,
                    amount: 0,
                    currency: v.currency,
                    interval: (hasMonthly ? 'year' : 'month') as 'month' | 'year',
                    isActive: true,
                    offer: '',
                    trialDays: 0,
                    isDefault: v.prices.length === 0 // First one gets default true
                };
                return { ...v, prices: [...v.prices, newPrice] };
            }
            return v;
        });
        setProduct({ ...product, variants: updatedVariants });
    };

    const handleSetDefaultPrice = (variantId: string, priceId: string) => {
        const updatedVariants = product.variants?.map(v => {
            if (v.id === variantId) {
                return {
                    ...v,
                    prices: v.prices.map(p => ({
                        ...p,
                        isDefault: p.id === priceId
                    }))
                };
            }
            return v;
        });
        setProduct({ ...product, variants: updatedVariants });
    };

    const handleUpdatePrice = (variantId: string, priceId: string, updates: any) => {
        const updatedVariants = product.variants?.map(v => {
            if (v.id === variantId) {
                return {
                    ...v,
                    prices: v.prices.map(p => p.id === priceId ? { ...p, ...updates } : p)
                };
            }
            return v;
        });
        setProduct({ ...product, variants: updatedVariants });
    };

    const handleDeletePrice = (variantId: string, priceId: string) => {
        const updatedVariants = product.variants?.map(v => {
            if (v.id === variantId) {
                return { ...v, prices: v.prices.filter(p => p.id !== priceId) };
            }
            return v;
        });
        setProduct({ ...product, variants: updatedVariants });
    };

    const handleUpdateVariant = (variantId: string, updates: Partial<any>) => {
        setProduct(prev => {
            const variants = prev.variants?.map(v => v.id === variantId ? { ...v, ...updates } : v) || [];
            return { ...prev, variants };
        });
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-[400px]">
            <div className="w-8 h-8 border-2 border-accent-primary border-t-transparent rounded-full animate-spin" />
        </div>
    );

    const selectedVariant = product.variants?.find(v => v.id === selectedRegion);
    const selectedCountry = selectedVariant ? AVAILABLE_COUNTRIES.find(c => c.code === selectedVariant.country) : null;

    return (
        <div className="h-full flex flex-col p-1 animate-fade-in max-w-5xl mx-auto w-full overflow-hidden">
            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/payments/products')}
                        className="btn-ghost !p-1.5"
                    >
                        <ArrowLeft size={16} />
                    </button>
                    <h1 className="text-xl font-bold text-neutral-900">
                        {product.name || 'New Product'}
                        {!isNew && product.id && (
                            <ProductIdDisplay id={product.id} />
                        )}
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    {!isNew && (
                        <>
                            <div className="flex items-center bg-neutral-100 p-0.5 rounded-lg border border-neutral-200/60 mr-2">
                                <button
                                    onClick={() => setProduct({ ...product, isActive: false })}
                                    className={`px-3 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md transition-all ${!product.isActive ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'}`}
                                >
                                    Draft
                                </button>
                                <button
                                    onClick={() => setProduct({ ...product, isActive: true })}
                                    className={`px-3 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md transition-all ${product.isActive ? 'bg-white text-green-600 shadow-sm' : 'text-neutral-500 hover:text-green-600/70'}`}
                                >
                                    Active
                                </button>
                            </div>
                            <div className="w-px h-5 bg-neutral-200"></div>
                            <div className="flex items-center bg-neutral-100 p-0.5 rounded-lg border border-neutral-200/60 mr-2 ml-2">
                                <button
                                    onClick={() => setProduct({ ...product, isDefault: false })}
                                    className={`px-3 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md transition-all ${!product.isDefault ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'}`}
                                >
                                    Standard
                                </button>
                                <button
                                    onClick={() => setProduct({ ...product, isDefault: true })}
                                    className={`px-3 py-1 text-[11px] font-bold uppercase tracking-wider rounded-md transition-all ${product.isDefault ? 'bg-white text-blue-600 shadow-sm' : 'text-neutral-500 hover:text-blue-600/70'}`}
                                >
                                    Default
                                </button>
                            </div>
                            <div className="w-px h-5 bg-neutral-200"></div>
                            <div className="relative mr-2 ml-2" ref={platformsMenuRef}>
                                <button
                                    onClick={() => setPlatformsMenuOpen(o => !o)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-md transition-all bg-neutral-100 border border-neutral-200/60 text-neutral-700 hover:bg-neutral-50"
                                    title="Choose which platforms can sell this product."
                                >
                                    <Settings size={12} />
                                    Sold on ({(product.availableOn ?? ['website']).length})
                                    <ChevronDown size={12} className={`transition-transform ${platformsMenuOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {platformsMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-64 bg-white border border-neutral-200 rounded-lg shadow-lg z-50 overflow-hidden animate-fade-in">
                                        <div className="px-3 py-2 border-b border-neutral-100">
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Available on</p>
                                        </div>
                                        <div className="py-1">
                                            {PLATFORM_OPTIONS.map(opt => {
                                                const selected = (product.availableOn ?? ['website']).includes(opt.key);
                                                const Icon = opt.icon;
                                                return (
                                                    <button
                                                        key={opt.key}
                                                        onClick={() => togglePlatform(opt.key)}
                                                        className="w-full flex items-center gap-3 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                                                    >
                                                        <span className={`w-4 h-4 rounded border flex items-center justify-center ${selected ? 'bg-accent-primary border-accent-primary' : 'border-neutral-300 bg-white'}`}>
                                                            {selected && <Check size={12} className="text-white" />}
                                                        </span>
                                                        <Icon size={14} className="text-neutral-500" />
                                                        <span className="flex-1 text-left">{opt.label}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="w-px h-5 bg-neutral-200"></div>
                            <button
                                onClick={() => setIsDuplicateModalOpen(true)}
                                className="btn-secondary !px-3 !py-1.5 !text-xs !text-neutral-600 hover:!bg-neutral-50 hover:!border-neutral-200 flex items-center gap-1.5"
                            >
                                <Copy size={12} />
                                Duplicate
                            </button>
                            <button
                                onClick={handleDelete}
                                className="btn-secondary !px-3 !py-1.5 !text-xs !text-red-600 hover:!bg-red-50 hover:!border-red-200 flex items-center gap-1.5"
                            >
                                <Trash2 size={12} />
                                Delete
                            </button>
                        </>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={saving || (!isNew && !isDirty)}
                        className={`btn-primary !px-3 !py-1.5 !text-xs flex items-center gap-1.5 ${(!isNew && !isDirty) ? 'opacity-50 cursor-not-allowed bg-neutral-300 border-neutral-300 text-neutral-500 hover:bg-neutral-300 hover:border-neutral-300' : ''}`}
                    >
                        {saving ? (
                            <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <Save size={12} />
                        )}
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex-shrink-0 bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden mb-6 flex divide-x divide-neutral-100">
                {[
                    { id: 'general', label: 'General Info', icon: Globe },
                    { id: 'pricing', label: 'Pricing & Regions', icon: MapIcon },
                    { id: 'features', label: 'Feature Config', icon: Settings },
                ].map(tab => {
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => {
                                if (isActive) return;
                                if (isDirty) {
                                    showConfirmation(
                                        'Unsaved Changes',
                                        'You have unsaved changes. Switching tabs will cause you to lose these changes unless you save first.',
                                        () => setActiveTab(tab.id as any),
                                        'warning',
                                        'Switch Anyway'
                                    );
                                } else {
                                    setActiveTab(tab.id as any);
                                }
                            }}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm transition-all relative group ${isActive
                                ? 'bg-neutral-50 font-medium text-neutral-900'
                                : 'text-neutral-500 hover:bg-neutral-50/50 hover:text-neutral-700'
                                }`}
                        >
                            <tab.icon size={16} className={isActive ? 'text-accent-primary' : 'text-neutral-400 group-hover:text-neutral-500'} />
                            {tab.label}
                            {isActive && (
                                <div className="absolute inset-x-0 bottom-0 h-0.5 bg-accent-primary" />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Content Body */}
            <div className="flex-1 overflow-y-auto min-h-0 space-y-6 pb-6">

                {activeTab === 'general' && (
                    <div className="animate-slide-up space-y-6 max-w-2xl mx-auto">
                        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-6 space-y-6">
                            <h2 className="text-sm font-bold text-neutral-900 flex items-center gap-2 mb-4">
                                <Globe size={18} className="text-accent-primary" />
                                Global Product Details
                            </h2>
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Base Product Name</label>
                                    <input
                                        type="text"
                                        value={product.name || ''}
                                        onChange={e => setProduct({ ...product, name: e.target.value })}
                                        className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary outline-none transition-all placeholder:text-neutral-400"
                                        placeholder="Internal Global Name (e.g. Pro Suite)"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Base Product Description</label>
                                    <textarea
                                        value={product.description || ''}
                                        onChange={e => setProduct({ ...product, description: e.target.value })}
                                        className="w-full px-4 py-3 bg-neutral-50 border border-neutral-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary outline-none transition-all placeholder:text-neutral-400 min-h-[120px] resize-none"
                                        placeholder="Internal Global Description..."
                                    />
                                </div>
                            </div>
                        </div>

                    </div>
                )}

                {activeTab === 'pricing' && (
                    <div className="animate-slide-up">
                        {!isNew ? (
                            <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden flex min-h-[500px]">
                                {/* Left Sidebar: Countries */}
                                <div className="w-64 border-r border-neutral-100 bg-neutral-50/30 flex flex-col">
                                    <div className="p-4 border-b border-neutral-100 flex items-center justify-between">
                                        <span className="text-xs font-bold text-neutral-500 uppercase tracking-wider">Regions</span>
                                        <button
                                            onClick={() => setIsAddRegionOpen(true)}
                                            className="p-1 hover:bg-neutral-200 rounded text-neutral-500 transition-colors"
                                            title="Add Region"
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>
                                    <div className="flex-1 overflow-y-auto p-2 space-y-1">
                                        {(product.variants?.length || 0) === 0 ? (
                                            <div className="text-center p-4">
                                                <p className="text-xs text-neutral-400 italic">No regions added yet.</p>
                                            </div>
                                        ) : product.variants?.map(variant => {
                                            const country = AVAILABLE_COUNTRIES.find(c => c.code === variant.country);
                                            const isSelected = selectedRegion === variant.id;
                                            return (
                                                <div
                                                    key={variant.id}
                                                    className={`w-full group relative flex items-center pr-2 rounded-lg transition-all ${isSelected
                                                        ? 'bg-white shadow-sm ring-1 ring-neutral-200'
                                                        : 'hover:bg-neutral-100/50'
                                                        }`}
                                                >
                                                    <button
                                                        onClick={() => setSelectedRegion(variant.id)}
                                                        className="flex-1 text-left px-3 py-2.5 flex items-center gap-3"
                                                    >
                                                        <span className="text-lg">{country?.emoji || '🌍'}</span>
                                                        <div className="flex-1 min-w-0">
                                                            <div className={`text-sm font-medium truncate ${isSelected ? 'text-neutral-900' : 'text-neutral-600'}`}>
                                                                {country?.name || variant.country}
                                                            </div>
                                                            <div className="text-[10px] text-neutral-400">
                                                                {variant.prices.length} prices • {variant.currency}
                                                            </div>
                                                        </div>
                                                    </button>

                                                    {(!variant.id.startsWith('temp_') || true) && (
                                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setRegionToDuplicate(variant.id);
                                                                    setIsDuplicateRegionModalOpen(true);
                                                                }}
                                                                className="p-1.5 text-neutral-400 hover:text-accent-primary hover:bg-accent-primary/10 rounded"
                                                                title="Duplicate Region"
                                                            >
                                                                <Copy size={14} />
                                                            </button>

                                                            {!variant.id.startsWith('temp_') && (
                                                                <button
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        showConfirmation(
                                                                            'Remove Region',
                                                                            `Are you sure you want to remove the ${country?.name} region? Current subscribers will lose access to this plan.`,
                                                                            async () => {
                                                                                try {
                                                                                    await productsApi.deleteVariant(variant.id);
                                                                                    // Refresh
                                                                                    loadProduct(product.id!);
                                                                                } catch (err: any) {
                                                                                    console.error(err);
                                                                                    if (err.response?.status === 409) {
                                                                                        showAlert('Error', 'Cannot delete this region because it has active subscriptions.', 'danger');
                                                                                    } else {
                                                                                        showAlert('Error', 'Failed to delete region', 'danger');
                                                                                    }
                                                                                }
                                                                            },
                                                                            'danger',
                                                                            'Remove'
                                                                        );
                                                                    }}
                                                                    className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded"
                                                                    title="Remove Region"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            )}

                                                            {variant.id.startsWith('temp_') && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const updatedVariants = product.variants?.filter(v => v.id !== variant.id);
                                                                        setProduct({ ...product, variants: updatedVariants });
                                                                        if (selectedRegion === variant.id) setSelectedRegion(null);
                                                                    }}
                                                                    className="p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded"
                                                                    title="Remove Unsaved Region"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Right Content: Pricing Details */}
                                <div className="flex-1 bg-white p-8">
                                    {selectedVariant && selectedCountry ? (
                                        <div className="max-w-xl mx-auto space-y-8">
                                            {/* Sub Tabs */}
                                            <div className="flex p-1 bg-neutral-100 rounded-lg">
                                                <button
                                                    onClick={() => setActivePricingTab('prices')}
                                                    className={`
                                                        flex-1 py-1.5 text-xs font-semibold rounded-md transition-all
                                                        ${activePricingTab === 'prices'
                                                            ? 'bg-white text-neutral-900 shadow-sm'
                                                            : 'text-neutral-500 hover:text-neutral-700'
                                                        }
                                                    `}
                                                >
                                                    Pricing
                                                </button>
                                                <button
                                                    onClick={() => setActivePricingTab('settings')}
                                                    className={`
                                                        flex-1 py-1.5 text-xs font-semibold rounded-md transition-all
                                                        ${activePricingTab === 'settings'
                                                            ? 'bg-white text-neutral-900 shadow-sm'
                                                            : 'text-neutral-500 hover:text-neutral-700'
                                                        }
                                                    `}
                                                >
                                                    Settings
                                                </button>
                                            </div>

                                            {/* Content */}
                                            {activePricingTab === 'prices' ? (
                                                <div className="grid gap-4 animate-fade-in">
                                                    {selectedVariant.prices.map(price => (
                                                        <div key={price.id} className={`relative group p-4 rounded-xl border transition-all flex items-start gap-4 ${price.isDefault ? 'border-accent-primary bg-accent-primary/5' : 'border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm'}`}>
                                                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold border mt-0.5 ${price.isDefault ? 'bg-white border-accent-primary/30 text-accent-primary' : 'bg-neutral-50 border-neutral-100 text-neutral-700'}`}>
                                                                {selectedCountry.symbol}
                                                            </div>
                                                            <div className="flex-1 space-y-4">
                                                                <div className="grid grid-cols-2 gap-4">
                                                                    <div>
                                                                        <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1 block">Price</label>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            step="0.01"
                                                                            value={(price.amount / 100) || 0}
                                                                            onChange={(e) => handleUpdatePrice(selectedVariant.id, price.id, { amount: Math.round(parseFloat(e.target.value || '0') * 100) })}
                                                                            className="w-full font-mono text-sm font-semibold text-neutral-900 bg-transparent border-b border-transparent focus:border-accent-primary outline-none py-1 hover:border-neutral-200 transition-colors"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1 block">Interval</label>
                                                                        <select
                                                                            value={price.interval}
                                                                            onChange={(e) => handleUpdatePrice(selectedVariant.id, price.id, { interval: e.target.value as 'month' | 'year' })}
                                                                            className="w-full text-sm font-medium text-neutral-900 bg-transparent border-b border-transparent focus:border-accent-primary outline-none py-1 hover:border-neutral-200 transition-colors cursor-pointer"
                                                                        >
                                                                            <option value="month">Monthly</option>
                                                                            <option value="year">Yearly</option>
                                                                        </select>
                                                                    </div>
                                                                    <div>
                                                                        <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1 block">Billing Interval</label>
                                                                        <select
                                                                            value={price.billing_interval || price.interval}
                                                                            onChange={(e) => handleUpdatePrice(selectedVariant.id, price.id, { billing_interval: e.target.value as 'month' | 'year' })}
                                                                            className="w-full text-sm font-medium text-neutral-900 bg-transparent border-b border-transparent focus:border-accent-primary outline-none py-1 hover:border-neutral-200 transition-colors cursor-pointer"
                                                                        >
                                                                            <option value="month">Monthly</option>
                                                                            <option value="year">Yearly</option>
                                                                        </select>
                                                                    </div>
                                                                </div>

                                                                <div className="grid grid-cols-[1fr_1fr_auto] gap-4 items-end">
                                                                    <div>
                                                                        <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1 block">Offer Label</label>
                                                                        <input
                                                                            type="text"
                                                                            value={price.offer || ''}
                                                                            onChange={(e) => handleUpdatePrice(selectedVariant.id, price.id, { offer: e.target.value })}
                                                                            className="w-full text-sm text-neutral-900 bg-transparent border-b border-transparent focus:border-accent-primary outline-none py-1 hover:border-neutral-200 transition-colors placeholder:text-neutral-300"
                                                                            placeholder="e.g. Save 20%"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1 block">Trial Days</label>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            value={price.trialDays || 0}
                                                                            onChange={(e) => handleUpdatePrice(selectedVariant.id, price.id, { trialDays: parseInt(e.target.value) || 0 })}
                                                                            className="w-full text-sm text-neutral-900 bg-transparent border-b border-transparent focus:border-accent-primary outline-none py-1 hover:border-neutral-200 transition-colors"
                                                                            placeholder="0"
                                                                        />
                                                                    </div>
                                                                    <div className="flex items-center gap-3 h-[29px]">
                                                                        <span className="text-xs font-medium text-neutral-500">Default Selection</span>
                                                                        <label className="relative inline-flex items-center cursor-pointer">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="sr-only peer"
                                                                                checked={price.isDefault || false}
                                                                                onChange={() => handleSetDefaultPrice(selectedVariant.id, price.id)}
                                                                            />
                                                                            <div className="w-9 h-5 bg-neutral-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-offset-2 peer-focus:ring-accent-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-accent-primary shadow-inner"></div>
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <button
                                                                onClick={() => handleDeletePrice(selectedVariant.id, price.id)}
                                                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-red-200 shadow-sm"
                                                            >
                                                                <X size={12} />
                                                            </button>
                                                        </div>
                                                    ))}

                                                    {selectedVariant.prices.length < 2 && (
                                                        <button
                                                            onClick={() => handleAddPrice(selectedVariant.id)}
                                                            className="w-full py-4 rounded-xl border-2 border-dashed border-neutral-200 text-neutral-400 text-sm font-medium hover:border-accent-primary hover:text-accent-primary hover:bg-accent-primary/5 transition-all flex items-center justify-center gap-2"
                                                        >
                                                            <Plus size={16} />
                                                            Add {selectedVariant.prices.length === 0 ? 'Monthly' : 'Yearly'} Plan
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="space-y-6 animate-fade-in">

                                                    <div className="space-y-2">
                                                        <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Region Plan Name</label>
                                                        <input
                                                            type="text"
                                                            value={selectedVariant.name || ''}
                                                            onChange={e => handleUpdateVariant(selectedVariant.id, { name: e.target.value })}
                                                            className="w-full px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary outline-none transition-all placeholder:text-neutral-300"
                                                            placeholder={`e.g. ${product.name || 'Pro'} (${selectedCountry?.code})`}
                                                        />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Region Description</label>
                                                        <textarea
                                                            value={selectedVariant.description || ''}
                                                            onChange={e => handleUpdateVariant(selectedVariant.id, { description: e.target.value })}
                                                            className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary outline-none transition-all placeholder:text-neutral-300 min-h-[100px] resize-none"
                                                            placeholder="Description for this specific region..."
                                                        />
                                                    </div>

                                                    <div className="space-y-2">
                                                        <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Slogan</label>
                                                        <input
                                                            type="text"
                                                            value={selectedVariant.slogan || ''}
                                                            onChange={e => handleUpdateVariant(selectedVariant.id, { slogan: e.target.value })}
                                                            className="w-full px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary outline-none transition-all placeholder:text-neutral-300"
                                                            placeholder="e.g. Best for small teams"
                                                        />
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-6">
                                                        <div className="space-y-2">
                                                            <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Display Priority</label>
                                                            <input
                                                                type="number"
                                                                value={selectedVariant.displayOrder || 0}
                                                                onChange={e => handleUpdateVariant(selectedVariant.id, { displayOrder: parseInt(e.target.value) || 0 })}
                                                                className="w-full px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary outline-none transition-all"
                                                            />
                                                            <p className="text-[10px] text-neutral-400">Higher number = shows last (or controls sorting)</p>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Highlight Plan</label>
                                                            <div className="flex items-center gap-3 h-[42px]">
                                                                <label className="relative inline-flex items-center cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="sr-only peer"
                                                                        checked={selectedVariant.isHighlighted || false}
                                                                        onChange={(e) => handleUpdateVariant(selectedVariant.id, { isHighlighted: e.target.checked })}
                                                                    />
                                                                    <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-offset-2 peer-focus:ring-accent-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-primary shadow-inner"></div>
                                                                </label>
                                                                <span className="text-sm text-neutral-600">{selectedVariant.isHighlighted ? 'Highlighted' : 'Normal'}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {selectedVariant.isHighlighted && (
                                                        <div className="space-y-2 animate-fade-in">
                                                            <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Highlight Text</label>
                                                            <input
                                                                type="text"
                                                                value={selectedVariant.highlightText || ''}
                                                                onChange={e => handleUpdateVariant(selectedVariant.id, { highlightText: e.target.value })}
                                                                className="w-full px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary outline-none transition-all placeholder:text-neutral-300"
                                                                placeholder="e.g. Most Popular"
                                                            />
                                                        </div>
                                                    )}

                                                    <div className="space-y-3 pt-4 border-t border-neutral-100">
                                                        <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Marketing Features</label>
                                                        <div className="space-y-2">
                                                            {(JSON.parse(selectedVariant.marketingFeatures || '[]') as string[]).map((feature, idx) => (
                                                                <div key={idx} className="flex gap-2 group">
                                                                    <input
                                                                        value={feature}
                                                                        onChange={(e) => {
                                                                            const features = JSON.parse(selectedVariant.marketingFeatures || '[]');
                                                                            features[idx] = e.target.value;
                                                                            handleUpdateVariant(selectedVariant.id, { marketingFeatures: JSON.stringify(features) });
                                                                        }}
                                                                        className="flex-1 px-3 py-2 bg-neutral-50 border border-transparent hover:bg-white hover:border-neutral-200 rounded-md text-sm outline-none focus:border-accent-primary focus:bg-white transition-all"
                                                                    />
                                                                    <button
                                                                        onClick={() => {
                                                                            const features = JSON.parse(selectedVariant.marketingFeatures || '[]');
                                                                            features.splice(idx, 1);
                                                                            handleUpdateVariant(selectedVariant.id, { marketingFeatures: JSON.stringify(features) });
                                                                        }}
                                                                        className="p-2 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                            <div className="flex gap-2">
                                                                <input
                                                                    type="text"
                                                                    value={newFeatureInput}
                                                                    onChange={e => setNewFeatureInput(e.target.value)}
                                                                    onKeyDown={e => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            if (newFeatureInput.trim()) {
                                                                                const features = JSON.parse(selectedVariant.marketingFeatures || '[]');
                                                                                features.push(newFeatureInput.trim());
                                                                                handleUpdateVariant(selectedVariant.id, { marketingFeatures: JSON.stringify(features) });
                                                                                setNewFeatureInput('');
                                                                            }
                                                                        }
                                                                    }}
                                                                    className="flex-1 px-3 py-2 bg-white border border-neutral-200 rounded-md text-sm outline-none focus:border-accent-primary transition-all placeholder:text-neutral-300"
                                                                    placeholder="Add a new feature..."
                                                                />
                                                                <button
                                                                    onClick={() => {
                                                                        if (newFeatureInput.trim()) {
                                                                            const features = JSON.parse(selectedVariant.marketingFeatures || '[]');
                                                                            features.push(newFeatureInput.trim());
                                                                            handleUpdateVariant(selectedVariant.id, { marketingFeatures: JSON.stringify(features) });
                                                                            setNewFeatureInput('');
                                                                        }
                                                                    }}
                                                                    className="px-4 py-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-600 rounded-md text-sm font-medium transition-colors"
                                                                >
                                                                    Add
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="h-full flex flex-col items-center justify-center text-neutral-400">
                                            <Globe size={48} className="text-neutral-200 mb-4" />
                                            <p className="text-sm">Select a region to configure pricing</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-16 text-center max-w-2xl mx-auto mt-10">
                                <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6 text-amber-500">
                                    <Save size={32} />
                                </div>
                                <h3 className="text-lg font-bold text-neutral-900 mb-2">Create Product First</h3>
                                <p className="text-sm text-neutral-500 mb-8">
                                    You need to specify a base name and save before configuring regional pricing.
                                </p>
                                <div className="space-y-4 max-w-sm mx-auto mb-8 text-left">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Base Product Name</label>
                                        <input
                                            type="text"
                                            value={product.name}
                                            onChange={e => setProduct({ ...product, name: e.target.value })}
                                            className="w-full px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary outline-none transition-all placeholder:text-neutral-300"
                                            placeholder="e.g. Pro Plan"
                                            autoFocus
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Base Description</label>
                                        <textarea
                                            value={product.description}
                                            onChange={e => setProduct({ ...product, description: e.target.value })}
                                            className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary outline-none transition-all placeholder:text-neutral-300 min-h-[100px] resize-none"
                                            placeholder="General description..."
                                        />
                                    </div>
                                </div>
                                <button onClick={handleSave} disabled={!product.name} className="btn-primary !px-8 h-10 shadow-lg shadow-accent-primary/20 disabled:opacity-50 disabled:cursor-not-allowed">Save & Continue</button>
                            </div>
                        )}
                    </div>
                )
                }

                {
                    activeTab === 'features' && (
                        <div className="animate-slide-up space-y-6">
                            {!isNew ? (
                                <>
                                    <FeatureManagerV2
                                        productId={product.id!}
                                        title="Global Defaults"
                                        ref={(el) => {
                                            if (el) featureManagersRef.current.set('global', el);
                                            else featureManagersRef.current.delete('global');
                                        }}
                                        onDirty={(dirty) => handleFeatureDirty('global', dirty)}
                                    />
                                    {(product.variants?.length || 0) > 0 && (
                                        <>
                                            <div className="flex items-center gap-4 py-2">
                                                <div className="h-px bg-neutral-200 flex-1" />
                                                <span className="text-xs font-bold text-neutral-400 uppercase tracking-widest">Regional Overrides</span>
                                                <div className="h-px bg-neutral-200 flex-1" />
                                            </div>
                                            <div className="grid gap-6">
                                                {product.variants!.map(variant => (
                                                    <FeatureManagerV2
                                                        key={variant.id}
                                                        productId={product.id!}
                                                        variantId={variant.id}
                                                        title={`${AVAILABLE_COUNTRIES.find(c => c.code === variant.country)?.name || variant.country} Overrides`}
                                                        ref={(el) => {
                                                            if (el) featureManagersRef.current.set(variant.id, el);
                                                            else featureManagersRef.current.delete(variant.id);
                                                        }}
                                                        onDirty={(dirty) => handleFeatureDirty(variant.id, dirty)}
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </>
                            ) : (
                                <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-16 text-center max-w-2xl mx-auto mt-10">
                                    <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6 text-amber-500">
                                        <Save size={32} />
                                    </div>
                                    <h3 className="text-lg font-bold text-neutral-900 mb-2">Create Product First</h3>
                                    <p className="text-sm text-neutral-500 mb-8">
                                        You need to specify a base name and save before configuring features.
                                    </p>
                                    <div className="space-y-4 max-w-sm mx-auto mb-8 text-left">
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Base Product Name</label>
                                            <input
                                                type="text"
                                                value={product.name}
                                                onChange={e => setProduct({ ...product, name: e.target.value })}
                                                className="w-full px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary outline-none transition-all placeholder:text-neutral-300"
                                                placeholder="e.g. Pro Plan"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Base Description</label>
                                            <textarea
                                                value={product.description}
                                                onChange={e => setProduct({ ...product, description: e.target.value })}
                                                className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary outline-none transition-all placeholder:text-neutral-300 min-h-[100px] resize-none"
                                                placeholder="General description..."
                                            />
                                        </div>
                                    </div>
                                    <button onClick={handleSave} disabled={!product.name} className="btn-primary !px-8 h-10 shadow-lg shadow-accent-primary/20 disabled:opacity-50 disabled:cursor-not-allowed">Save & Continue</button>
                                </div>
                            )}
                        </div>
                    )
                }
            </div >

            {/* Modals */}
            {
                isAddRegionOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={() => setIsAddRegionOpen(false)}>
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-up" onClick={e => e.stopPropagation()}>
                            <div className="px-6 py-5 border-b border-neutral-100 flex items-center justify-between bg-neutral-50/50">
                                <h3 className="text-lg font-bold text-neutral-900">Add Region</h3>
                                <button onClick={() => setIsAddRegionOpen(false)} className="text-neutral-400 hover:text-neutral-600 transition-colors">
                                    <X size={20} />
                                </button>
                            </div>
                            <div className="max-h-[60vh] overflow-y-auto p-2">
                                {AVAILABLE_COUNTRIES
                                    .filter(c => !product.variants?.find(v => v.country === c.code))
                                    .map(country => (
                                        <button
                                            key={country.code}
                                            onClick={() => {
                                                handleAddVariant(country);
                                                setIsAddRegionOpen(false);
                                            }}
                                            className="w-full text-left px-4 py-3 hover:bg-neutral-50 rounded-xl flex items-center gap-4 transition-colors group"
                                        >
                                            <div className="text-2xl">{country.emoji}</div>
                                            <div className="flex-1">
                                                <div className="text-sm font-semibold text-neutral-900">{country.name}</div>
                                                <div className="text-xs text-neutral-400">{country.currency} ({country.symbol})</div>
                                            </div>
                                            <Plus size={16} className="text-neutral-300 group-hover:text-accent-primary" />
                                        </button>
                                    ))}
                                {AVAILABLE_COUNTRIES.filter(c => !product.variants?.find(v => v.country === c.code)).length === 0 && (
                                    <div className="p-8 text-center text-sm text-neutral-500">
                                        All available regions have been added.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Duplicate Region Modal */}
            {
                isDuplicateRegionModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={() => setIsDuplicateRegionModalOpen(false)}>
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-up" onClick={e => e.stopPropagation()}>
                            <div className="px-6 py-5 border-b border-neutral-100 flex items-center justify-between bg-neutral-50/50">
                                <h3 className="text-lg font-bold text-neutral-900">Duplicate Region</h3>
                                <button onClick={() => {
                                    setIsDuplicateRegionModalOpen(false);
                                    setRegionToDuplicate(null);
                                }} className="text-neutral-400 hover:text-neutral-600 transition-colors">
                                    <X size={20} />
                                </button>
                            </div>
                            <div className="max-h-[60vh] overflow-y-auto p-2">
                                {AVAILABLE_COUNTRIES
                                    .filter(c => !product.variants?.find(v => v.country === c.code))
                                    .map(country => (
                                        <button
                                            key={country.code}
                                            onClick={() => {
                                                if (regionToDuplicate) {
                                                    handleAddVariant(country, regionToDuplicate);
                                                }
                                                setIsDuplicateRegionModalOpen(false);
                                                setRegionToDuplicate(null);
                                            }}
                                            className="w-full text-left px-4 py-3 hover:bg-neutral-50 rounded-xl flex items-center gap-4 transition-colors group"
                                        >
                                            <div className="text-2xl">{country.emoji}</div>
                                            <div className="flex-1">
                                                <div className="text-sm font-semibold text-neutral-900">{country.name}</div>
                                                <div className="text-xs text-neutral-400">{country.currency} ({country.symbol})</div>
                                            </div>
                                            <Copy size={16} className="text-neutral-300 group-hover:text-accent-primary" />
                                        </button>
                                    ))}
                                {AVAILABLE_COUNTRIES.filter(c => !product.variants?.find(v => v.country === c.code)).length === 0 && (
                                    <div className="p-8 text-center text-sm text-neutral-500">
                                        All available regions have been added.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }
            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={modalConfig.isOpen}
                title={modalConfig.title}
                message={modalConfig.message}
                variant={modalConfig.variant}
                confirmText={modalConfig.confirmText}
                cancelText={modalConfig.cancelText}
                onConfirm={() => {
                    modalConfig.onConfirm();
                    setModalConfig(prev => ({ ...prev, isOpen: false }));
                }}
                onCancel={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
            />
            {/* Duplicate Modal */}
            <DuplicateModal
                isOpen={isDuplicateModalOpen}
                onClose={() => setIsDuplicateModalOpen(false)}
                onConfirm={handleDuplicateConfirm}
                initialName={product.name || ''}
                isSubmitting={isDuplicating}
            />
        </div>
    );
};
